
// Extend window to include posthog
declare global {
  interface Window {
    posthog?: any;
  }
}

export class PostHogIntegration {
  private useNativePostHog: boolean;

  constructor() {
    // Check if native PostHog is available
    this.useNativePostHog = typeof window !== 'undefined' && 
                           window.posthog && 
                           typeof window.posthog.init === 'function';
  }

  isAvailable(): boolean {
    return this.useNativePostHog && 
           window.posthog && 
           typeof window.posthog.capture === 'function' &&
           typeof window.posthog.identify === 'function' &&
           typeof window.posthog.opt_in_capturing === 'function';
  }

  setConsent(hasConsent: boolean): void {
    if (!this.isAvailable()) return;

    try {
      if (hasConsent) {
        window.posthog.opt_in_capturing();
      } else {
        window.posthog.opt_out_capturing();
      }
    } catch (error) {
      console.warn('PostHog consent update failed:', error);
    }
  }

  track(event: string, properties: Record<string, any>): boolean {
    if (!this.isAvailable()) return false;

    try {
      window.posthog.capture(event, properties);
      return true;
    } catch (error) {
      console.warn('PostHog track failed:', error);
      return false;
    }
  }

  identify(userId?: string, userProperties?: Record<string, any>): boolean {
    if (!this.isAvailable()) return false;

    try {
      window.posthog.identify(userId, userProperties);
      return true;
    } catch (error) {
      console.warn('PostHog identify failed:', error);
      return false;
    }
  }

  page(properties: Record<string, any>): boolean {
    if (!this.isAvailable()) return false;

    try {
      window.posthog.capture('$pageview', properties);
      return true;
    } catch (error) {
      console.warn('PostHog page tracking failed:', error);
      return false;
    }
  }

  pageLeave(properties: Record<string, any>): boolean {
    if (!this.isAvailable()) return false;

    try {
      window.posthog.capture('$pageleave', properties);
      return true;
    } catch (error) {
      console.warn('PostHog page leave failed:', error);
      return false;
    }
  }

  alias(authenticatedId: string, anonymousId: string): boolean {
    if (!this.isAvailable()) return false;

    try {
      window.posthog.alias(authenticatedId, anonymousId);
      return true;
    } catch (error) {
      console.warn('PostHog alias failed:', error);
      return false;
    }
  }

  setUser(userId: string | null): void {
    if (!this.isAvailable()) return;

    try {
      if (userId) {
        window.posthog.identify(userId);
      } else {
        // Clear user data on logout
        window.posthog.identify(null);
        // Also clear any stored user properties
        if (typeof window.posthog.unregister === 'function') {
          window.posthog.unregister(['$email', '$name', '$username']);
        }
      }
    } catch (error) {
      console.warn('PostHog user update failed:', error);
    }
  }

  getFeatureFlag(flagKey: string): boolean | string | undefined {
    if (!this.isAvailable()) return undefined;

    try {
      return window.posthog.getFeatureFlag(flagKey);
    } catch (error) {
      console.warn('PostHog getFeatureFlag failed:', error);
      return undefined;
    }
  }

  isFeatureEnabled(flagKey: string): boolean {
    if (!this.isAvailable()) return false;

    try {
      return window.posthog.isFeatureEnabled(flagKey);
    } catch (error) {
      console.warn('PostHog isFeatureEnabled failed:', error);
      return false;
    }
  }

  startSessionRecording(): void {
    if (!this.isAvailable()) return;

    try {
      window.posthog.startSessionRecording();
    } catch (error) {
      console.warn('PostHog startSessionRecording failed:', error);
    }
  }

  stopSessionRecording(): void {
    if (!this.isAvailable()) return;

    try {
      window.posthog.stopSessionRecording();
    } catch (error) {
      console.warn('PostHog stopSessionRecording failed:', error);
    }
  }
}
